import { createActionGroup, emptyProps, props } from '@ngrx/store';

import { ActionSource } from 'src/app/state/action-sources';
import {
    BlueSageUnmatchedPaymentRowFragment,
    GetBlueSageJobLookupQuery,
    GetBlueSageUnmatchedPaymentsQuery,
    GetBlueSageUnmatchedSummaryQuery,
    TransactionType,
} from 'src/generated/graphql.generated';

export const BlueSageQueueActions = createActionGroup({
    source: ActionSource.BLUESAGE_QUEUE,
    events: {
        'Page Opened': emptyProps(),
        'Refresh Button Clicked': emptyProps(),
        'Status Filter Changed': props<{ status: string | undefined }>(),
        'Page Changed': props<{ skip: number; limit: number }>(),

        'Load Payments Success': props<{
            page: GetBlueSageUnmatchedPaymentsQuery['blueSageUnmatchedPayments'];
        }>(),
        'Load Payments Error': props<{ error: Error }>(),

        'Load Summary Success': props<{
            summary: GetBlueSageUnmatchedSummaryQuery['blueSageUnmatchedPaymentsSummary'];
        }>(),
        'Load Summary Error': props<{ error: Error }>(),

        'Payment Selected': props<{ paymentId: string | undefined }>(),

        // Requirement: K25 - the operator types a job CODE and the id is looked
        // up; only a resolved id ever reaches the money-moving mutation. See the
        // state file for why this used to be an id and why that was wrong.
        'Job Code Changed': props<{ jobCode: string }>(),
        'Job Lookup Success': props<{
            match: GetBlueSageJobLookupQuery['blueSageJobLookup'];
            /** Correlation key: the code + paymentId this lookup was for. The
             * reducer uses it to discard a success from a superseded lookup. */
            lookupKey: { code: string; paymentId: string | null };
        }>(),
        'Job Lookup Error': props<{ error: Error }>(),

        'Dismiss Reason Changed': props<{ dismissReason: string }>(),

        // `fileAs`, not `type`: every NgRx action already carries a `type`, and a
        // prop of the same name collapses the action's type to `never`.
        'Attach Button Clicked': props<{ paymentId: string; fileAs?: TransactionType }>(),
        'Attach Success': props<{ payment: BlueSageUnmatchedPaymentRowFragment }>(),
        'Attach Error': props<{ error: Error }>(),

        // Complete one of the resolved job's open holds with this capture
        // (2026-09-03) instead of filing a second transaction beside it. Its own
        // action and its own mutation: attaching creates a row, this settles one
        // that already exists, and they must never be confused in the log.
        'Complete Hold Button Clicked': props<{ paymentId: string; transactionId: string }>(),
        'Complete Hold Success': props<{ payment: BlueSageUnmatchedPaymentRowFragment }>(),
        'Complete Hold Error': props<{ error: Error }>(),

        // The job an attached row names. Its own action rather than a bare router
        // link, because "go to the job this money landed on" is an event worth
        // seeing in the log — it is how somebody checks an attribution.
        // Requirement: an ATTACHED queue row names the job the money went to and
        // links to it. A resolved row that cannot say where the payment landed
        // leaves an operator no way back to it, which is the whole reason the row
        // records the job rather than merely disappearing from the open list.
        'Attached Job Clicked': props<{ jobId: string }>(),

        'Dismiss Button Clicked': props<{ paymentId: string }>(),
        'Dismiss Success': props<{ payment: BlueSageUnmatchedPaymentRowFragment }>(),
        'Dismiss Error': props<{ error: Error }>(),
    },
});
